import {
  ContentRegistrationType,
  UserInfo,
  XMLGenerationStrategy,
} from '@/common/types';
import { FormData } from '@content-registration/types';
import {
  createCrossrefDocument,
  appendElement,
  appendElementIf,
  serializeXml,
} from '../xml-helpers';
import {
  buildContributors,
  buildTitles,
  buildPublicationDates,
  buildDoiData,
  buildIssns,
  buildCrossmark,
  buildHead,
} from '../builders';
import type {
  PostPublicationUpdate,
  PublicationDates,
} from '@generated/form-types/post-publication-update';

/**
 * Returns the earliest publication date as a YYYY-MM-DD string.
 */
function earliestPublicationDate(dates: PublicationDates): string {
  const { online, print } = dates;

  if (online && print) {
    return online <= print ? online : print;
  }

  if (online) return online;
  if (print) return print;

  // should never happen since the form requires at least one publication date
  throw new Error('At least one publication date is required.');
}

export class PostPublicationUpdateXmlRenderingStrategy
  implements XMLGenerationStrategy
{
  async generateXML(
    _recordType: ContentRegistrationType,
    formData: FormData,
    prettyPrint = false,
    userInfo?: UserInfo
  ): Promise<string> {
    const data = formData as unknown as PostPublicationUpdate;
    const notice = data.updateNotice;
    const journal = data.journal;

    const doc = createCrossrefDocument();
    const root = doc.documentElement;

    // <head>
    buildHead(root, userInfo);

    // <body>
    const body = appendElement(root, 'body');
    const journalEl = appendElement(body, 'journal');

    // <journal_metadata>
    const journalMetadata = appendElement(journalEl, 'journal_metadata');

    appendElement(journalMetadata, 'full_title', journal.titles.fullTitle);
    appendElementIf(
      journalMetadata,
      'abbrev_title',
      journal.titles.abbreviatedTitle
    );

    buildIssns(journalMetadata, journal.issn);

    // <journal_article publication_type="full_text">
    const journalArticle = appendElement(journalEl, 'journal_article', null, {
      publication_type: 'full_text',
    });

    // 1. titles
    buildTitles(journalArticle, notice.title);

    // 2. contributors (optional)
    if (notice.contributors && notice.contributors.length > 0) {
      buildContributors(journalArticle, notice.contributors);
    }

    // 3. publication_date
    buildPublicationDates(journalArticle, notice.publicationDates);

    // 4. crossmark
    buildCrossmark(
      journalArticle,
      journal.crossmarkPolicyPage,
      notice.updateType,
      earliestPublicationDate(notice.publicationDates),
      notice.updatedArticleDoi
    );

    // 5. doi_data
    buildDoiData(journalArticle, notice.doi, notice.resourceUrl);

    return serializeXml(doc, prettyPrint);
  }
}
